import { appURLs } from "@/app/app-urls.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import { GAME_SYMBOL_MARVEL_RIVALS } from "@/game-marvel-rivals/definition-symbol.mjs";
import type { HeroesStats } from "@/game-marvel-rivals/models/heroes-stats.mjs";
import type { Match } from "@/game-marvel-rivals/models/match.mjs";
import type { Matchlist } from "@/game-marvel-rivals/models/matchlist.mjs";
import type { Profile } from "@/game-marvel-rivals/models/profile.mjs";
import routes from "@/game-marvel-rivals/routes.mjs";
import GameIconBoxMarvelRivals from "@/inline-assets/marvel-rivals-logo.svg";

export { GAME_SYMBOL_MARVEL_RIVALS };

const definition = {
  gameSymbol: GAME_SYMBOL_MARVEL_RIVALS,
  friendlyName: ["common:games.marvel.long", "Marvel Rivals"],
  fullName: ["common:games.marvel.long", "Marvel Rivals"],
  shortName: "marvel-rivals" as const,
  gameColor: "#6969a7",
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/marvelrivals.webp`,
  iconShape: GameIconBoxMarvelRivals,
  iconBox: GameIconBoxMarvelRivals,
  iconBadge: GameIconBoxMarvelRivals,
  icon: `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/games/small/marvel-rivals-small.svg`,
  isActive: true as const,
  preferFriendlyName: false as const,
  settings: () => import("@/game-marvel-rivals/Settings.jsx"),
  IPC: () => import("@/game-marvel-rivals/init.mjs"),
  navLinks: [
    {
      href: "/marvel-rivals/heroes",
      text: ["common:heroes", "Heroes"],
    },
    {
      href: "/marvel-rivals/tierlist",
      text: ["marvelrivals:tierlist", "Tierlist"],
    },
    {
      href: "/marvel-rivals/maps",
      text: ["common:maps", "Maps"],
    },
    {
      href: "/marvel-rivals/overlays",
      text: ["common:navigation.overlays", "Overlays"],
    },
    {
      href: "/marvel-rivals/patch-notes",
      text: ["contentful:patchNotes", "Patch Notes"],
    },
  ],
  routes,
  promoFeatures: [],
  meta: {
    game: {
      title: ["home:meta.home.mr.title", "Marvel Rivals"],
      description: [
        "home:meta.home.mr.description",
        "Use Blitz.gg's Marvel Rivals Tracker to track your stats, monitor your performance, and win more games. Download our desktop app for the best tracking tools and premium real-time overlays!",
      ],
    },
    bg: `${appURLs.CDN}/homepage/mrbg.webp`,
    bgVideo: `${appURLs.CDN_VIDEOS}/homepage/marvel-rivals-homepage-bg-video.mp4`,
    players: "40M+",
    matches: "1M+",
    features: [
      {
        title: ["marvelrivals:tierlist", "Tierlist"],
        description: [
          "marvelrivals:features.tierlist",
          "Find the best heroes in Marvel Rivals",
        ],
        image: `${appURLs.CDN}/homepage/feature-mr-tierlist.webp`,
        link: {
          href: "/marvel-rivals/tierlist",
          text: ["marvelrivals:link.viewHeroStats", "View Tierlist"],
        },
      },
      {
        title: ["marvelrivals:heroStats", "Hero Stats"],
        description: [
          "marvelrivals:meta.features.heroes",
          "Analyze Marvel Rivals hero stats, including win rates, pick rates, and rankings",
        ],
        image: `${appURLs.CDN}/homepage/feature-mr-heroes.webp`,
        link: {
          href: "/marvel-rivals/heroes",
          text: ["marvelrivals:link.viewHeroStats", "View Hero Stats"],
        },
      },
      // {
      //   title: ["common:maps", "Maps"],
      //   description: [
      //     "marvelrivals:features.maps",
      //     "Master every map in Marvel Rivals with our in-depth guides, strategies, and pro tips",
      //   ],
      //   image: `${appURLs.CDN}/homepage/feature-mr-maps.webp`,
      //   link: {
      //     href: "/marvel-rivals/maps",
      //     text: ["marvelrivals:link.viewHeroStats", "View Maps"],
      //   },
      // },
    ],
    homeData: () => import("@/game-marvel-rivals/fetches/homepage.mjs"),
  },
  homepageFeatures: [
    "blitz-search-hidden",
    {
      type: "contentful-articles",
      title: ["marvelrivals:contentfularticles.title", "News & Patch Notes"],
      description: [
        "marvelrivals:contentfularticles.description",
        "What’s new with Marvel Rivals",
      ],
      cta: {
        href: "/marvel-rivals/patch-notes",
        title: ["common:viewAllNews", "View All News"],
      },
    },
    {
      type: "contentful-grid",
      title: ["marvelrivals:contentfulgrid.title", "Latest Guides"],
      description: [
        "marvelrivals:contentfulgrid.description",
        "Stay up to date with the latest Marvel Rivals hero guides!",
      ],
      cta: {
        href: "/marvel-rivals/heroes",
        title: ["marvelrivals:contentfulgrid.cta.title", "View Heroes"],
      },
    },
    "blitz-game-features",
  ],
} satisfies GameDefinition<typeof GAME_SYMBOL_MARVEL_RIVALS>;

export default definition;

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    marvelrivals: {
      tierlist: unknown;
      profiles: Record<string, Profile>;
      matchlist: Record<string, Matchlist>;
      matches: Record<string, Match>;
      heroesStats: Record<string, HeroesStats>;
      dataSeason: number;
    };
  }
}
